/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import { MtmaContact } from 'src/app/model/mtma-contact';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { CustomValidators } from 'src/app/validators/custom.validators';
import moment from 'moment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { MtmaRule } from 'src/app/model/mtma-rules-access';
import { selectMtmaRulesAll } from 'src/app/store/device/device.selector';

@Component({
	selector: 'app-mtma-manage-contact-dialog',
	templateUrl: './mtma-manage-contact-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class MtmaContactDialogComponent implements OnInit, OnDestroy {
	contactForm: FormGroup;

	mtmaContactModifySub: Subscription;
	contact: MtmaContact;

	adding = true;

	mtmaActionsSub: Subscription;

	mtmaActions: MtmaAction[] = [];

	listRules: MtmaRule[];
	listRulesSub: Subscription;

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined && config.data.contact !== undefined) {
			this.contact = config.data.contact;
			this.adding = false;
		} else {
			this.contact = new MtmaContact();
		}
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getMtmaRulesAll());

		this.subscribeAll();
		this.buildForm();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll() {
		this.mtmaContactModifySub = this.store.select('device', 'mtmaContactModify')
			.pipe(filter(mtmaContactModifySub => !!mtmaContactModifySub))
			.subscribe(() => {
				this.resetClose();
			});
		this.mtmaActionsSub = this.store.select('device', 'mtmaActions')
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.Contacts);
			});
		this.listRulesSub = this.store.select(selectMtmaRulesAll).subscribe(listRules => {
			if (listRules === undefined) {
				this.listRules = [];
			} else {
				this.listRules = Object.assign([], listRules);
			}
			const rule = new MtmaRule();
			rule.Name = this.translate.instant('NO_GROUP');
			rule.Id = null;
			this.listRules.unshift(rule);
		});
	}

	buildForm() {
		this.contactForm = new FormGroup({
			'Description': new FormControl(this.contact.Description, [Validators.maxLength(48)]),
			'Name': new FormControl(this.contact.Name, [Validators.required, Validators.maxLength(35)]),
			'Phone': new FormControl(this.contact.Phone, [Validators.required, Validators.minLength(7),
			Validators.maxLength(20), Validators.pattern('[0-9+]+')]),
			'Action1': new FormControl(this.contact.Actions[0], Validators.required),
			'Action2': new FormControl(this.contact.Actions[1], Validators.required),
			'Rule': new FormControl(this.contact.RuleId),
			'StartDate': new FormControl(this.contact.DtFrom === undefined ? null : moment(this.contact.DtFrom).toDate()),
			'StartTime': new FormControl(this.getTime(this.contact.DtFrom)),
			'EndDate': new FormControl(this.contact.DtTo === undefined ? null : moment(this.contact.DtTo).toDate()),
			'EndTime': new FormControl(this.getTime(this.contact.DtTo)),
		});
		this.contactForm.addValidators(CustomValidators.checkMatchingMtmaActionContact('Action1', 'Action2'));
		this.contactForm.addValidators(CustomValidators.checkLeadingPlus('Phone'));
		this.contactForm.addValidators(CustomValidators.checkMatchingTemporaryRule('Rule', 'StartTime', 'EndTime', 'StartDate', 'EndDate'));
	}

	ngOnDestroy(): void {
		this.mtmaContactModifySub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.listRulesSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetContactForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.contactForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.contactForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		let startTime = ''
		if (typeof this.f.StartTime.value === 'string') {
			startTime = this.f.StartTime.value;
		} else {
			startTime = moment(this.f.StartTime.value).format('HH:mm');
		}
		let endTime = ''
		if (typeof this.f.EndTime.value === 'string') {
			endTime = this.f.EndTime.value;
		} else {
			endTime = moment(this.f.EndTime.value).format('HH:mm');
		}
		this.contact = {
			...this.contact,
			Description: this.f.Description.value,
			Name: this.f.Name.value,
			Phone: this.f.Phone.value,
			Actions: [this.f.Action1.value, this.f.Action2.value],
			RuleId: this.f.Rule.value === undefined || this.f.Rule.value === null ? 0 : this.f.Rule.value
		};
		if (this.contact.RuleId !== null && this.contact.RuleId !== undefined &&
			this.contactForm.value.StartDate != null && this.contactForm.value.StartTime != null &&
			this.contactForm.value.EndDate != null && this.contactForm.value.EndTime != null) {
			//DATE VAL
			this.contact.DtFrom = moment(this.contactForm.value.StartDate).format('yyyy-MM-DDT') + startTime;
			this.contact.DtTo = moment(this.contactForm.value.EndDate).format('yyyy-MM-DDT') + endTime;
		} else {
			this.contact.DtFrom = undefined;
			this.contact.DtTo = undefined;
		}
		if (this.contact.ObjId) {
			this.store.dispatch(DeviceActions.editMtmaContact({ contact: this.contact }));
		} else {
			if (this.contact.RuleId === 0) {
				this.contact.RuleId = null;
			}
			this.store.dispatch(DeviceActions.addMtmaContact({ contact: this.contact }));
		}
	}

	getTime(date: string): string {
		if (date === undefined) {
			return '';
		}
		return moment(date).format('HH:mm');
	}

	get f(): any {
		return this.contactForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaContactDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaContactDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}
